import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FileUp, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useMeasurements } from "@/hooks/useMeasurements";
import { useEstimateCalculations } from "@/hooks/useEstimateCalculations";
import { DXFUploader } from "./DXFUploader";
import { DXFPreview } from "./DXFPreview";
import { LayerMappingDialog } from "./LayerMappingDialog";
import { ScaleCalibrationDialog } from "./ScaleCalibrationDialog";

interface MeasurementFormProps {
  estimateId?: string;
}

const MeasurementForm = ({ estimateId }: MeasurementFormProps) => {
  const { measurements, createMeasurement, updateMeasurement } = useMeasurements(estimateId);
  const { calculateSquaresNeeded } = useEstimateCalculations([], 0);

  const [areaSqft, setAreaSqft] = useState("");
  const [pitch, setPitch] = useState("");
  const [layers, setLayers] = useState("1");
  const [valleyLf, setValleyLf] = useState("");
  const [ridgeLf, setRidgeLf] = useState("");
  const [rakeLf, setRakeLf] = useState("");
  const [eaveLf, setEaveLf] = useState("");
  const [hipLf, setHipLf] = useState("");
  const [flashingLf, setFlashingLf] = useState("");
  const [skylightCount, setSkylightCount] = useState("0");
  const [chimneyCount, setChimneyCount] = useState("0");
  const [ventCount, setVentCount] = useState("0");
  const [notes, setNotes] = useState("");
  
  const [dxfData, setDxfData] = useState<any>(null);
  const [showDXFUploader, setShowDXFUploader] = useState(false);
  const [showDXFPreview, setShowDXFPreview] = useState(false);
  const [showLayerMapping, setShowLayerMapping] = useState(false);
  const [showScaleCalibration, setShowScaleCalibration] = useState(false);
  const [dataSource, setDataSource] = useState<'manual' | 'dxf' | 'edited'>('manual');

  const existingMeasurement = measurements && measurements.length > 0 ? measurements[0] : null;

  useEffect(() => {
    if (measurements && measurements.length > 0) {
      const m = measurements[0];
      setAreaSqft(m.area_sqft?.toString() || "");
      setPitch(m.pitch || "");
      setLayers(m.layers?.toString() || "1");
      setValleyLf(m.valley_lf?.toString() || "");
      setRidgeLf(m.ridge_lf?.toString() || "");
      setRakeLf(m.rake_lf?.toString() || "");
      setEaveLf(m.eave_lf?.toString() || "");
      setHipLf(m.hip_lf?.toString() || "");
      setFlashingLf(m.flashing_lf?.toString() || "");
      setSkylightCount(m.skylight_count?.toString() || "0");
      setChimneyCount(m.chimney_count?.toString() || "0");
      setVentCount(m.vent_count?.toString() || "0");
      setNotes(m.notes || "");
      setDataSource((m as any).source || 'manual');
      if ((m as any).dxf_data) {
        setDxfData((m as any).dxf_data);
      }
    }
  }, [measurements]);

  const squares = areaSqft ? calculateSquaresNeeded(parseFloat(areaSqft)) : 0;

  const getSourceBadge = () => {
    if (dataSource === 'dxf') {
      return <Badge variant="default" className="ml-2">DXF</Badge>;
    } else if (dataSource === 'edited') {
      return <Badge variant="secondary" className="ml-2">EDITED</Badge>;
    } else if (dataSource === 'manual') {
      return <Badge variant="outline" className="ml-2">MANUAL</Badge>;
    }
    return null;
  };

  const handleDXFUploadSuccess = (data: any) => {
    setDxfData(data);
    setShowDXFUploader(false);
    setShowDXFPreview(true);
  };

  const handleDXFAccept = async () => {
    if (!dxfData || !estimateId) return;

    // Check if this is XML data (new format) or DXF data (old format)
    const isXmlData = dxfData.measurements && !dxfData.summary;

    if (isXmlData) {
      // Handle XML data (new format)
      const m = dxfData.measurements;
      setAreaSqft(m.area_sqft?.toString() || "");
      setPitch(m.pitch || "");
      setValleyLf(m.valley_lf?.toString() || "");
      setRidgeLf(m.ridge_lf?.toString() || "");
      setHipLf(m.hip_lf?.toString() || "");
      setRakeLf(m.rake_lf?.toString() || "");
      setEaveLf(m.eave_lf?.toString() || "");
      setFlashingLf(((m.stepflashing_lf || 0) + (m.flashing_lf || 0)).toString());
      setSkylightCount("0"); // Not available in XML yet
      setChimneyCount("0");  // Not available in XML yet
      setVentCount("0");     // Not available in XML yet
    } else {
      // Handle DXF data (old format)
      setAreaSqft(dxfData.summary.totalSurfaceAreaSqFt.toString());
      setPitch(dxfData.summary.pitchStrategy.replace('global:', ''));
      setValleyLf(dxfData.lengthsLF.valley.toString());
      setRidgeLf(dxfData.lengthsLF.ridge.toString());
      setHipLf(dxfData.lengthsLF.hip.toString());
      setRakeLf(dxfData.lengthsLF.rake.toString());
      setEaveLf(dxfData.lengthsLF.eave.toString());
      setFlashingLf(dxfData.lengthsLF.wallStepFlashing.toString());
      setSkylightCount(dxfData.penetrations.skylights.length.toString());
      setChimneyCount(dxfData.penetrations.chimneys.length.toString());
      setVentCount((dxfData.penetrations.pipes.length + dxfData.penetrations.fans).toString());
    }

    setDataSource('dxf');
    setShowDXFPreview(false);

    // Save to database
    let measurementData: any;

    if (isXmlData) {
      // Save XML data
      const m = dxfData.measurements;
      measurementData = {
        estimate_id: estimateId,
        measurement_type: "roof",
        area_sqft: m.area_sqft,
        pitch: m.pitch,
        valley_lf: m.valley_lf,
        ridge_lf: m.ridge_lf,
        hip_lf: m.hip_lf,
        rake_lf: m.rake_lf,
        eave_lf: m.eave_lf,
        flashing_lf: (m.stepflashing_lf || 0) + (m.flashing_lf || 0),
        skylight_count: 0,
        chimney_count: 0,
        vent_count: 0,
        source: 'dxf',
        notes: dxfData.facets ? `Facet breakdown:\n${dxfData.facets.map((f: any) => `${f.id}: ${f.area} sq ft @ ${f.pitch}`).join('\n')}` : notes
      };
    } else {
      // Save DXF data (old format)
      measurementData = {
        estimate_id: estimateId,
        measurement_type: "roof",
        area_sqft: dxfData.summary.totalSurfaceAreaSqFt,
        pitch: dxfData.summary.pitchStrategy.replace('global:', ''),
        valley_lf: dxfData.lengthsLF.valley,
        ridge_lf: dxfData.lengthsLF.ridge,
        hip_lf: dxfData.lengthsLF.hip,
        rake_lf: dxfData.lengthsLF.rake,
        eave_lf: dxfData.lengthsLF.eave,
        flashing_lf: dxfData.lengthsLF.wallStepFlashing,
        skylight_count: dxfData.penetrations.skylights.length,
        chimney_count: dxfData.penetrations.chimneys.length,
        vent_count: dxfData.penetrations.pipes.length + dxfData.penetrations.fans,
        dxf_data: dxfData,
        dxf_preview_svg: dxfData.previewSvg,
        waste_factor: dxfData.summary.wasteFactor,
        drip_edge_lf: dxfData.lengthsLF.dripEdge,
        wall_step_lf: dxfData.lengthsLF.wallStepFlashing,
        pipe_count: dxfData.penetrations.pipes.length,
        fan_count: dxfData.penetrations.fans,
        source: 'dxf',
        scale_factor: dxfData.scale,
        units: dxfData.units,
        notes: notes
      };
    }

    if (existingMeasurement) {
      await updateMeasurement.mutateAsync({ id: existingMeasurement.id, ...measurementData });
    } else {
      await createMeasurement.mutateAsync(measurementData);
    }

    const successDesc = isXmlData
      ? `${dxfData.measurements.area_sqft} sq ft, ${dxfData.measurements.ridge_lf} LF ridge`
      : `${dxfData.summary.squares.toFixed(2)} squares, ${dxfData.lengthsLF.ridge.toFixed(1)} LF ridge`;

    toast.success("Measurements imported", {
      description: successDesc
    });
  };

  const handleDXFReject = () => {
    setShowDXFPreview(false);
    setDxfData(null);
  };

  const handleAdjustMapping = () => {
    setShowLayerMapping(true);
  };

  const handleLayerMappingApply = (mappings: Record<string, string>, saveAsDefault: boolean) => {
    // Re-parse with new mappings (would need to re-upload to edge function)
    toast.success("Layer mappings updated", {
      description: "Please re-upload the DXF file to apply new mappings."
    });
  };

  const handleScaleCalibration = () => {
    setShowScaleCalibration(true);
  };

  const handleScaleCalibrationApply = (scaleFactor: number) => {
    // Re-parse with new scale factor
    toast.success("Scale calibrated", {
      description: `Scale factor: ${scaleFactor.toFixed(4)}`
    });
  };

  const handleFieldChange = (field: string) => {
    // Mark as edited if source was DXF
    if (dataSource === 'dxf') {
      setDataSource('edited');
    }
  };

  const handleSave = async () => {
    if (!estimateId) return;

    const measurementData: any = {
      estimate_id: estimateId,
      measurement_type: "roof",
      area_sqft: areaSqft ? parseFloat(areaSqft) : null,
      pitch: pitch || null,
      layers: layers ? parseInt(layers) : null,
      valley_lf: valleyLf ? parseFloat(valleyLf) : null,
      ridge_lf: ridgeLf ? parseFloat(ridgeLf) : null,
      rake_lf: rakeLf ? parseFloat(rakeLf) : null,
      eave_lf: eaveLf ? parseFloat(eaveLf) : null,
      hip_lf: hipLf ? parseFloat(hipLf) : null,
      flashing_lf: flashingLf ? parseFloat(flashingLf) : null,
      skylight_count: parseInt(skylightCount),
      chimney_count: parseInt(chimneyCount),
      vent_count: parseInt(ventCount),
      notes: notes || null,
      source: dataSource,
    };

    if (measurements && measurements.length > 0) {
      await updateMeasurement.mutateAsync({ id: measurements[0].id, ...measurementData });
    } else {
      await createMeasurement.mutateAsync(measurementData);
    }
  };

  return (

    <div className="space-y-6">
      {showDXFUploader && (
        <DXFUploader
          onSuccess={handleDXFUploadSuccess}
          onError={(error) => {
            toast.error("DXF upload failed", { description: error });
            setShowDXFUploader(false);
          }}
        />
      )}

      {showDXFPreview && dxfData && (
        <DXFPreview
          data={dxfData}
          onAccept={handleDXFAccept}
          onReject={handleDXFReject}
          onAdjustMapping={handleAdjustMapping}
          onScaleCalibration={handleScaleCalibration}
        />
      )}

      {showLayerMapping && dxfData && (
        <LayerMappingDialog
          open={showLayerMapping}
          onOpenChange={setShowLayerMapping}
          detectedLayers={dxfData.diagnostics?.unmappedLayers || []}
          currentMappings={{}}
          onApply={handleLayerMappingApply}
        />
      )}

      {showScaleCalibration && dxfData && (
        <ScaleCalibrationDialog
          open={showScaleCalibration}
          onOpenChange={setShowScaleCalibration}
          previewSvg={dxfData.previewSvg}
          onApply={handleScaleCalibrationApply}
        />
      )}

      {!showDXFUploader && !showDXFPreview && (
        <div className="flex gap-2">
          <Button
            onClick={() => setShowDXFUploader(true)}
            variant="outline"
            className="w-full"
          >
            <FileUp className="h-4 w-4 mr-2" />
            Import from DXF/XML
          </Button>
          {dxfData && (
            <Button
              onClick={() => setShowDXFPreview(true)}
              variant="outline"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Re-import
            </Button>
          )}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            Roof Measurements
            {getSourceBadge()}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="areaSqft">Total Area (sq ft) *</Label>
              <Input
                id="areaSqft"
                type="number"
                value={areaSqft}
                onChange={(e) => setAreaSqft(e.target.value)}
                onBlur={handleSave}
                placeholder="2500"
              />
              {areaSqft && (
                <p className="text-sm text-muted-foreground">
                  ≈ {squares} squares (with 10% waste)
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="pitch">Pitch</Label>
              <Select value={pitch} onValueChange={(val) => { setPitch(val); handleSave(); }}>
                <SelectTrigger id="pitch">
                  <SelectValue placeholder="Select pitch" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3/12">3/12</SelectItem>
                  <SelectItem value="4/12">4/12</SelectItem>
                  <SelectItem value="5/12">5/12</SelectItem>
                  <SelectItem value="6/12">6/12</SelectItem>
                  <SelectItem value="7/12">7/12</SelectItem>
                  <SelectItem value="8/12">8/12</SelectItem>
                  <SelectItem value="9/12">9/12</SelectItem>
                  <SelectItem value="10/12">10/12</SelectItem>
                  <SelectItem value="12/12">12/12</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="layers">Existing Layers</Label>
              <Input
                id="layers"
                type="number"
                value={layers}
                onChange={(e) => setLayers(e.target.value)}
                onBlur={handleSave}
                min="1"
                max="3"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Linear Measurements</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="valleyLf">Valley (LF)</Label>
              <Input
                id="valleyLf"
                type="number"
                value={valleyLf}
                onChange={(e) => setValleyLf(e.target.value)}
                onBlur={handleSave}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ridgeLf">Ridge (LF)</Label>
              <Input
                id="ridgeLf"
                type="number"
                value={ridgeLf}
                onChange={(e) => setRidgeLf(e.target.value)}
                onBlur={handleSave}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hipLf">Hip (LF)</Label>
              <Input
                id="hipLf"
                type="number"
                value={hipLf}
                onChange={(e) => setHipLf(e.target.value)}
                onBlur={handleSave}
                placeholder="0"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="rakeLf">Rake (LF)</Label>
              <Input
                id="rakeLf"
                type="number"
                value={rakeLf}
                onChange={(e) => setRakeLf(e.target.value)}
                onBlur={handleSave}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="eaveLf">Eave (LF)</Label>
              <Input
                id="eaveLf"
                type="number"
                value={eaveLf}
                onChange={(e) => setEaveLf(e.target.value)}
                onBlur={handleSave}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="flashingLf">Flashing (LF)</Label>
              <Input
                id="flashingLf"
                type="number"
                value={flashingLf}
                onChange={(e) => setFlashingLf(e.target.value)}
                onBlur={handleSave}
                placeholder="0"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Penetrations & Features</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="skylightCount">Skylights</Label>
              <Input
                id="skylightCount"
                type="number"
                value={skylightCount}
                onChange={(e) => setSkylightCount(e.target.value)}
                onBlur={handleSave}
                min="0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="chimneyCount">Chimneys</Label>
              <Input
                id="chimneyCount"
                type="number"
                value={chimneyCount}
                onChange={(e) => setChimneyCount(e.target.value)}
                onBlur={handleSave}
                min="0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ventCount">Vents</Label>
              <Input
                id="ventCount"
                type="number"
                value={ventCount}
                onChange={(e) => setVentCount(e.target.value)}
                onBlur={handleSave}
                min="0"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={handleSave}
              placeholder="Add any additional notes about the measurements..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MeasurementForm;
